/**
 * Ecom payment methods — admin-managed registry mirroring `payout-deductions`'s
 * `PayoutMethod`. Each method is a `(currency × settlement)` pairing the admin
 * exposes to members at checkout:
 *
 *  - `currency`     pricing currency + which slot-wallet column to debit
 *                   ('cash' → available, 'gc' → available_gc, 'coin' → available_coin).
 *  - `settlement`   how the order is paid:
 *                     'wallet'  → debit the slot wallet inside the order tx,
 *                                 order completes immediately, comp event fires.
 *                     'manual'  → order lands at `pending`; member then uploads
 *                                 proof via `POST /payments/manual` from the
 *                                 order detail page; admin approves; existing
 *                                 manual-payment flow takes over from there.
 *                     'gateway' → online gateway (Dragonpay / iPay88). Order lands
 *                                 at `to_pay`; the member SPA calls
 *                                 `POST /payments/gateway/initiate`, performs the
 *                                 returned handoff (redirect / form-post), and a
 *                                 verified server-to-server webhook marks it paid
 *                                 + fires comp — no admin approval. Cash/PHP only.
 *                     'cod_manual' → Cash On Delivery, fully admin-controlled. The
 *                                 order is created straight at `to_ship` (Ready to
 *                                 Ship) with NO upfront payment/proof; stock is
 *                                 deducted at checkout. No comp event fires until an
 *                                 admin marks the order `completed`, at which point
 *                                 PV + wallet commissions release (regardless of the
 *                                 tenant `ecom_event_on` policy). Cash only.
 *
 * Config lives in `tenant_config.features.ecom_payment_methods.methods[]` (no
 * dedicated table) — same shape pattern as the payout-method registry, with a
 * typed admin editor on Tenant Settings (no raw JSON).
 */

import type { GatewayProvider } from './payment-gateways';

export type EcomPaymentMethodCurrency = 'cash' | 'gc' | 'coin';
export type EcomPaymentMethodSettlement = 'wallet' | 'manual' | 'gateway' | 'cod_manual';

export type EcomPaymentMethod = {
  /** Stable slug stored on the order (`orders.payment_method_code`). */
  code: string;
  /** User-facing name shown in the checkout picker (e.g. "Bank Transfer"). */
  label: string;
  currency: EcomPaymentMethodCurrency;
  settlement: EcomPaymentMethodSettlement;
  /**
   * For `settlement='manual'`: where the member sends the money (e.g.
   * "BPI · 1234-5678 · Tenant Holdings"). Ignored for wallet methods.
   */
  accountLabel?: string;
  /**
   * For `settlement='manual'`: free-text instructions shown above the proof
   * upload form on the order detail page. Markdown not interpreted — plain
   * paragraphs only.
   */
  instructions?: string;
  /**
   * Optional R2 object key for the method's logo (uploaded via the admin editor).
   * Served through `GET /storefront/payment-method-logo/:code`. When absent, the
   * checkout picker falls back to a text-only row.
   */
  logoKey?: string;
  /**
   * For `settlement='manual'`: optional R2 object key for a scannable payment QR
   * image the admin uploads (e.g. their GCash / Maya QR). Served through
   * `GET /storefront/payment-method-qr/:code` and shown to the buyer above the
   * proof-upload form so they can scan to pay. Absent = no QR shown.
   */
  qrKey?: string;
  /**
   * For `settlement='gateway'`: which online provider drives this method. Secrets
   * (merchant id / API key / merchant key) live in env, never here — this is only
   * the dispatch selector for the provider registry.
   */
  gatewayProvider?: GatewayProvider;
  /**
   * For `settlement='gateway'`: optional pre-selected payment channel —
   * Dragonpay `ProcId` or iPay88 `PaymentId` (e.g. a GCash code). Blank = let the
   * customer choose the channel on the gateway's page (recommended).
   */
  gatewayPaymentId?: string;
  /**
   * For `settlement='cod_manual'`: the courier id (from the ACTIVE shipping
   * config's `couriers[].id`) auto-assigned to COD orders so shipping is priced
   * at checkout even though no courier was chosen during the (absent) online
   * payment step. MUST reference an enabled, COD-eligible courier (`cod: true`).
   */
  defaultCourierId?: string;
  enabled: boolean;
};

export type EcomPaymentMethodsConfig = {
  methods: EcomPaymentMethod[];
};

/** Empty registry — admin populates it from Tenant Settings on first run. */
export const ECOM_PAYMENT_METHODS_DEFAULT: EcomPaymentMethodsConfig = {
  methods: [],
};

/**
 * Resolve a method by code from the registry. Returns `null` when the code is
 * absent or the method is disabled — callers should surface `unknown_payment_method`
 * either way (disabled methods must not silently fall back to defaults).
 */
export function resolveEcomPaymentMethod(
  cfg: EcomPaymentMethodsConfig | null | undefined,
  code: string,
): EcomPaymentMethod | null {
  if (!cfg) return null;
  const methods = Array.isArray(cfg.methods) ? cfg.methods : [];
  const found = methods.find((m) => m.code === code);
  if (!found || !found.enabled) return null;
  return found;
}

/** Build a blank, enabled cash-wallet method for the admin editor's "add row". */
export function makeEcomPaymentMethod(code = '', label = ''): EcomPaymentMethod {
  return {
    code,
    label,
    currency: 'cash',
    settlement: 'wallet',
    accountLabel: '',
    instructions: '',
    enabled: true,
  };
}
